import { BoxComponent } from '../../../base/box/Box';
import { BaseContainer } from '../../../base/container/BaseContainer';
import Carousel from '../../../components/Carousel/Carousel';
import { ICON_PLACEMENT } from '../../../components/Carousel/carousel.types';
import CtaButton from '../../../components/CtaButton';
import {
  ContentPosition,
  heroVariantOneInitialProps,
  ASPECT_RATIO,
  CORNER_VARIANTS,
  RadiusEnum,
  resolveSectionBackgroundColor,
} from '../../Commons';
import { EditableContent, Image } from '../../SharedComponents';
import ImageLoader from '../../SharedComponents/ImageLoader/ImageLoader';
import Video from '../../SharedComponents/Video';
import { HeroVariantOneProps } from '../types/index';
import { styles } from './HeroSectionVariantTwo.styles';
import {
  createBackgroundOverlayStyles,
  createButtonStyles,
  createSecondaryButtonStyles,
  createSectionHeightStyles,
} from '../utils/heroUtils';
import { useResponsiveStyles } from '../../../utils/useResponsiveStyles';
import AnimateOnView from '../../../components/AnimateOnView';
import { useHeroState } from '../hooks/useHeroState';
import { createBuilderClickHandler, createButtonClickHandler } from '../../../utils/builderUtils';

const HeroSectionVariantTwo = ({
  globalStyles = heroVariantOneInitialProps.globalStyles,
  localStyles = heroVariantOneInitialProps.localStyles,
  componentConfig = heroVariantOneInitialProps.componentConfig,
  builderConfig = heroVariantOneInitialProps.builderConfig,
  setComponentData,
  MediaMap,
  onTextSelect,
}: HeroVariantOneProps) => {
  const { builderMode, previewMode, isBuilderDesktopMode, isPublishedDesktopMode, pageUrls, seoTitle } = builderConfig;

  const designPreview = !builderConfig?.builderMode && builderConfig?.previewMode;

  const {
    video,
    image,
    carousel,
    heading,
    button,
    subheading,
    bodyText,
    overline,
    variant,
    sectionHeight,
    contentPosition,
    secondaryButton,
    secondaryImage,
    overlayStyles,
    customHeight = 0,
  } = componentConfig;

  const backgroundColor = resolveSectionBackgroundColor(
    {
      background: { value: componentConfig?.overlayStyles?.overlayColor },
      backgroundImage: { toggle: true, value: 'image', overlayColor: componentConfig?.overlayStyles?.overlayColor },
    },
    globalStyles
  );
  const isDesktop = Boolean(isBuilderDesktopMode || isPublishedDesktopMode);

  const { currentIndex, isAutoPlay, getCurrentIndex, getCurrentActiveState } = useHeroState();
  const { createResponsiveStyles } = useResponsiveStyles(isDesktop, localStyles, globalStyles);

  const h1Styles = {
    ...styles.Heading,
    ...globalStyles.heading,
    ...localStyles.heading,
  };

  const h1StylesResponsive = createResponsiveStyles(h1Styles, 'heading');

  const subHeadingTextStyles = {
    ...styles.HeroSectionSubHeading,
    ...globalStyles.subHeading,
    ...localStyles.subheading,
  };

  const subheadingTextStylesResponsive = createResponsiveStyles(subHeadingTextStyles, 'subheading');

  const overlineTextStyles = {
    ...styles.HeroSectionOverline,
    ...globalStyles.bodyText,
    ...localStyles.overline,
  };

  const overlineTextStylesMobile = createResponsiveStyles(overlineTextStyles, 'overline');

  const buttonStyles = createButtonStyles(button, globalStyles, localStyles, isDesktop);
  const secondaryButtonStyles = secondaryButton
    ? createSecondaryButtonStyles(secondaryButton, globalStyles, localStyles, isDesktop)
    : {};
  const bodyTextStyles = {
    ...styles.HeroSectionParagraph,
    ...globalStyles.bodyText,
    ...localStyles.bodyText,
  };

  const buttonWidthStyles = {
    ...buttonStyles,
    ...(!isDesktop && button?.fullWidth ? { width: '100%', textAlign: 'center' } : {}),
  };

  const secondaryButtonWidthStyles = {
    ...secondaryButtonStyles,
    ...(!isDesktop && secondaryButton?.fullWidth ? { width: '100%', textAlign: 'center' } : {}),
  };

  const bodyTextStylesResponsive = createResponsiveStyles(bodyTextStyles, 'bodyText');

  const imageRadiusConfig = localStyles?.imageRadius ?? globalStyles.imageRadius;

  const imageRadius =
    imageRadiusConfig?.radius === 'custom'
      ? imageRadiusConfig.value
      : imageRadiusConfig?.radius === 'soft'
      ? CORNER_VARIANTS[RadiusEnum.SOFT]
      : CORNER_VARIANTS[RadiusEnum.SHARP];

  const sectionHeightStyles = createSectionHeightStyles(
    sectionHeight,
    isDesktop,
    customHeight,
    builderMode,
    designPreview || false,
    '100vh'
  );
  const backgroundOverlayStyles = createBackgroundOverlayStyles(overlayStyles);

  const getPositionStyles = (position: ContentPosition = ContentPosition.LEFT) => {
    const positionStyles = {
      [ContentPosition.LEFT]: {
        alignItems: 'flex-start',
        textAlign: 'left' as const,
        justifyContent: 'flex-start',
        flexDirection: 'row-reverse',
        margin: '0 auto',
        paddingLeft: '2rem',
      },
      [ContentPosition.CENTER]: {
        alignItems: 'center',
        textAlign: 'center' as const,
        justifyContent: 'center',
        margin: 'auto',
        paddingLeft: '0',
        paddingRight: '0',
      },
      [ContentPosition.RIGHT]: {
        alignItems: 'flex-start',
        textAlign: 'left' as const,
        justifyContent: 'flex-start',
        flexDirection: 'row',
        margin: 'auto',
        paddingRight: '2rem',
      },
    };

    return positionStyles[position];
  };

  const positionStyles = getPositionStyles(contentPosition || ContentPosition.LEFT) as Record<string, any>;
  const headingContainerStyles = {
    ...styles.Container,
    ...positionStyles,
  };

  const heroContainerStyles = {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    ...sectionHeightStyles,
  };

  const handleTextSelect = (dataKey: string) => (data: any) => {
    if (onTextSelect && data) {
      onTextSelect({ ...data, dataKey });
    } else if (onTextSelect && data === null) {
      onTextSelect(null);
    }
  };

  const headingDataKey = variant === 'CAROUSEL' ? `carousel.value.${currentIndex.toString()}.heading` : 'heading.text';
  const subheadingDataKey =
    variant === 'CAROUSEL' ? `carousel.value.${currentIndex.toString()}.subheading` : 'subheading.text';
  const overlineDataKey =
    variant === 'CAROUSEL' ? `carousel.value.${currentIndex.toString()}.overline` : 'overline.text';
  const bodyTextDataKey =
    variant === 'CAROUSEL' ? `carousel.value.${currentIndex.toString()}.bodyText` : 'bodyText.text';

  const renderBackground = () => {
    switch (variant) {
      case 'VIDEO':
        return (
          video.toggle && (
            <Video
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover' as const,
                zIndex: 0,
              }}
              playsInline={true}
              autoPlay
              muted
              lazyLoadPlaceholder
              loop
              controls
              videoUrl={video.value}
              mediaMap={MediaMap}
              placeholderUrl={video.placeholder}
            />
          )
        );
      case 'STILL_IMAGE':
        return (
          image.toggle &&
          (image.loading && builderMode ? (
            <BoxComponent
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                zIndex: 0,
              }}
            >
              <ImageLoader globalStyles={localStyles} />
            </BoxComponent>
          ) : (
            <Image
              imageUrl={image.croppedFileUrl || image.value}
              altText={image.altText || seoTitle || image.name}
              lazyLoad={false}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover' as const,
                zIndex: 0,
              }}
              MediaMap={MediaMap}
              builderMode={builderMode}
            />
          ))
        );
      case 'CAROUSEL':
        return (
          carousel.toggle && (
            <BoxComponent
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
              }}
            >
              <Carousel
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover' as const,
                  zIndex: 0,
                }}
                images={carousel.value}
                MediaMap={MediaMap}
                isBuilderDesktopMode={isDesktop}
                iconPlacement={ICON_PLACEMENT.ON_IMAGE}
                autoScroll={!isAutoPlay}
                seoTitle={seoTitle}
                returnIndex={getCurrentIndex}
                overlayStyles={overlayStyles}
                initialIndex={currentIndex}
              />
            </BoxComponent>
          )
        );
      default:
        return null;
    }
  };

  const renderSecondaryImage = () => {
    if (secondaryImage && secondaryImage.toggle) {
      return (
        <BoxComponent
          sx={{
            ...(isDesktop ? styles.secondaryImage : styles.secondaryImage_Mobile),
            pointerEvents: 'auto',
          }}
          onClick={createBuilderClickHandler(onTextSelect, 'image')}
          style={{ cursor: builderMode ? 'pointer' : 'default' }}
        >
          {secondaryImage?.loading && builderMode ? (
            <ImageLoader globalStyles={localStyles} />
          ) : (
            <Image
              imageUrl={secondaryImage?.croppedFileUrl || secondaryImage?.value}
              altText={secondaryImage?.altText}
              MediaMap={MediaMap}
              lazyLoad={false}
              builderMode={builderMode}
              style={{
                aspectRatio: ASPECT_RATIO.ABOUT_US.VARIANT1[0],
                width: '100%',
                height: '100%',
                borderRadius: `${imageRadius}px`,
              }}
            />
          )}
        </BoxComponent>
      );
    }
    return null;
  };

  return (
    <BoxComponent>
      <BoxComponent sx={styles.HeroWrapper}>
        <BoxComponent sx={heroContainerStyles}>
          {/* Background media - absolute positioned */}
          <BoxComponent
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 0,
            }}
            onClick={builderMode && !previewMode ? createBuilderClickHandler(onTextSelect, 'background') : undefined}
          >
            {renderBackground()}
          </BoxComponent>
          {/* Overlay - only for non-carousel variants, absolute positioned, sibling of background */}
          {/* For carousel, overlay is handled inside Carousel component */}
          {variant !== 'CAROUSEL' && (
            <BoxComponent
              id="2"
              sx={backgroundOverlayStyles}
              onClick={builderMode && !previewMode ? createBuilderClickHandler(onTextSelect, 'background') : undefined}
            />
          )}
          {/* Content - relative positioned */}
          <BaseContainer
            className="section-content"
            sx={{
              ...(isDesktop ? headingContainerStyles : styles.container_Mobile),
              position: 'relative',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <AnimateOnView
              animateOnMount={false}
              animateOnScroll={true}
              previewMode={previewMode}
              builderMode={builderMode}
              globalStyles={globalStyles}
              sx={{
                ...(isDesktop ? styles.textContainer : styles.textContainer_Mobile),
                ...(isDesktop && contentPosition === ContentPosition.CENTER && { width: '100%', paddingTop: 0 }),
                pointerEvents: 'auto',
              }}
            >
              {overline?.toggle && (
                <EditableContent
                  dataKey={overlineDataKey}
                  sx={{
                    ...overlineTextStylesMobile,
                    inlineText:
                      variant === 'CAROUSEL'
                        ? localStyles?.carousel?.value?.[currentIndex]?.overline?.inlineText
                        : localStyles?.overline?.inlineText,
                  }}
                  previewMode={previewMode}
                  builderMode={builderMode}
                  setComponentData={setComponentData}
                  getCurrentActiveState={getCurrentActiveState}
                  text={variant === 'CAROUSEL' ? carousel.value[currentIndex]?.overline : overline?.text}
                  Element="body1"
                  onTextSelect={handleTextSelect(overlineDataKey)}
                  isDesktop={isDesktop}
                  backgroundColor={backgroundColor}
                  textAlignment={secondaryImage?.toggle ? 'left' : 'center'}
                />
              )}
              {heading.toggle && (
                <EditableContent
                  dataKey={headingDataKey}
                  sx={{
                    ...h1StylesResponsive,
                    inlineText:
                      variant === 'CAROUSEL'
                        ? localStyles?.carousel?.value?.[currentIndex]?.heading?.inlineText
                        : localStyles?.heading?.inlineText,
                  }}
                  previewMode={previewMode}
                  builderMode={builderMode}
                  setComponentData={setComponentData}
                  getCurrentActiveState={getCurrentActiveState}
                  text={variant === 'CAROUSEL' ? carousel.value[currentIndex]?.heading : heading.text}
                  Element="h1"
                  onTextSelect={handleTextSelect(headingDataKey)}
                  isDesktop={isDesktop}
                  backgroundColor={backgroundColor}
                  textAlignment={secondaryImage?.toggle ? 'left' : 'center'}
                />
              )}
              {subheading?.toggle && (
                <EditableContent
                  dataKey={subheadingDataKey}
                  sx={{
                    ...subheadingTextStylesResponsive,
                    inlineText:
                      variant === 'CAROUSEL'
                        ? localStyles?.carousel?.value?.[currentIndex]?.subheading?.inlineText
                        : localStyles?.subheading?.inlineText,
                  }}
                  previewMode={previewMode}
                  builderMode={builderMode}
                  setComponentData={setComponentData}
                  text={variant === 'CAROUSEL' ? carousel.value[currentIndex]?.subheading : subheading.text}
                  getCurrentActiveState={getCurrentActiveState}
                  Element="h2"
                  onTextSelect={handleTextSelect(subheadingDataKey)}
                  isDesktop={isDesktop}
                  backgroundColor={backgroundColor}
                  textAlignment={secondaryImage?.toggle ? 'left' : 'center'}
                />
              )}
              {bodyText && bodyText.toggle && (
                <EditableContent
                  dataKey={bodyTextDataKey}
                  sx={{
                    ...bodyTextStylesResponsive,
                    inlineText:
                      variant === 'CAROUSEL'
                        ? localStyles?.carousel?.value?.[currentIndex]?.bodyText?.inlineText
                        : localStyles?.bodyText?.inlineText,
                  }}
                  previewMode={previewMode}
                  builderMode={builderMode}
                  setComponentData={setComponentData}
                  text={variant === 'CAROUSEL' ? carousel.value[currentIndex]?.bodyText : bodyText.text}
                  getCurrentActiveState={getCurrentActiveState}
                  Element="body1"
                  onTextSelect={handleTextSelect(bodyTextDataKey)}
                  isDesktop={isDesktop}
                  backgroundColor={backgroundColor}
                  textAlignment={secondaryImage?.toggle ? 'left' : 'center'}
                />
              )}
              <BoxComponent
                sx={
                  isDesktop
                    ? {
                        ...styles.btnContainer,
                        ...getPositionStyles(
                          contentPosition === ContentPosition.LEFT || contentPosition === ContentPosition.RIGHT
                            ? ContentPosition.RIGHT
                            : ContentPosition.CENTER
                        ),
                      }
                    : styles.btnContainer_Mobile
                }
              >
                {button?.toggle && (
                  <BoxComponent
                    sx={{
                      display: 'inline-flex',
                      width: isDesktop ? 'auto' : '100%',
                      justifyContent: isDesktop ? 'flex-start' : 'center',
                    }}
                  >
                    <CtaButton
                      builderMode={builderMode}
                      previewMode={previewMode}
                      buttonConfig={button}
                      buttonStyles={buttonWidthStyles}
                      pageUrls={pageUrls}
                      onButtonClick={createButtonClickHandler(onTextSelect)}
                      dataKey="button.text"
                    />
                  </BoxComponent>
                )}
                {secondaryButton?.toggle && (
                  <BoxComponent
                    sx={{
                      display: 'inline-flex',
                      width: isDesktop ? 'auto' : '100%',
                      justifyContent: isDesktop ? 'flex-start' : 'center',
                    }}
                  >
                    <CtaButton
                      builderMode={builderMode}
                      previewMode={previewMode}
                      buttonConfig={secondaryButton}
                      buttonStyles={secondaryButtonWidthStyles}
                      pageUrls={pageUrls}
                      onButtonClick={createButtonClickHandler(onTextSelect)}
                      dataKey="secondaryButton.text"
                    />
                  </BoxComponent>
                )}
              </BoxComponent>
            </AnimateOnView>
            {renderSecondaryImage()}
          </BaseContainer>
        </BoxComponent>
      </BoxComponent>
    </BoxComponent>
  );
};

export default HeroSectionVariantTwo;
